import { Component, ComponentRef, EventEmitter, Input, Output, Signal } from '@angular/core';
import { DsBannerState, DsButtonVariants, Icons, IconStyles } from '@levelaccess/design-system';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@client/app/services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '@shared/interfaces/feature-flag.interface';
import {
  ACCESS_ENGINE_SHADOW_DOM_SEPARATOR_MATCHER,
  AccessibilityAuditToolNames,
  IFRAME_DOM_SEPARATOR_MATCHER,
} from '@shared/constants/audit-tool';
import { RuleAuditHistoryType } from '@shared/constants/rule-audit-history';
import { ColorContrastRuleSuggestionsComponent } from '@client/app/components/color-contrast-rule-suggestions/color-contrast-rule-suggestions.component';
import { ModalService } from '@client/app/services/modal.service';
import { $flaw } from '@shared/constants/flaw';
import { FlawService } from '@client/app/services/flaw.service';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { UrlFactory } from '@client/app/components/share-issue-report-url/share-issue-report-url.component';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { $auditFinding } from '@shared/constants/audit-finding';
import { TranslateService } from '@client/app/translate/translate.service';
import { CommonUtility } from '@client/app/utility/common.utility';
import { AlertType } from '@client/app/components/common-alert-box/common-alert-box.component';
import { UserDigitalPropertyService } from '@client/app/services/user-digital-property.service';
import { AngularUtility } from '@client/app/utility/angular.utility';
import { $scanIssues } from '@shared/constants/scan-issues';
import { IAutomatedScanIssue, IScanIssueAuditData, IScanIssues } from '@shared/interfaces/scan-issues.interface';
import { FindingContentBlockService } from '@client/app/services/issue-finding-details/finding-content-block.service';
import { IFindingContentBlock } from '@client/app/interfaces/finding.interface';

@Component({
  selector: 'app-issues-view-details',
  templateUrl: './issues-view-details.component.html',
  styleUrls: ['./issues-view-details.component.scss'],
  standalone: false,
})
export class IssuesViewDetailsComponent {
  @Input()
  public issue: IAutomatedScanIssue;
  @Input()
  public ruleDetails: IScanIssues;
  @Input()
  public scanId: string;
  @Input()
  public toolName: AccessibilityAuditToolNames;
  @Input()
  public isDigitalAssetFindingsEnabled: boolean;
  @Output()
  public dismissFinding: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public restoreFinding: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public createTask: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public navigatedAway: EventEmitter<void> = new EventEmitter<void>();

  public issueType: RuleAuditHistoryType = RuleAuditHistoryType.issue;
  public $scanIssues: typeof $scanIssues;
  public $flaw: typeof $flaw;
  public url: UrlFactory;
  public readonly AccessibilityAuditToolNames: typeof AccessibilityAuditToolNames;

  public isAccessEngineErrorTextEnabled: Signal<boolean> = toSignal(
    this.featureFlagService.variation$(FeatureFlagCollection.fixFindingDetails).pipe(map(Boolean)),
  );

  public isFindingsDetailsRefreshEnabled: Signal<boolean> = toSignal(
    this.featureFlagService.variation$(FeatureFlagCollection.findingsDetailsRefresh, false).pipe(map(Boolean)),
    { initialValue: false },
  );

  public displayIssueDescription(): string {
    const errorText: string | null = this.issue[$flaw.data][$auditFinding.errorText];
    return this.isAccessEngineErrorTextEnabled() && SharedCommonUtility.notNullishOrEmpty(errorText)
      ? errorText
      : this.ruleDetails[$scanIssues.description];
  }

  public isMobileTypeSignal: Signal<boolean> = toSignal(this.userDigitalPropertyService.isMobileDigitalProperty$, {
    initialValue: false,
  });

  public whereIsItLocatedText(): string {
    return this.isMobileTypeSignal() ? `${this.issue.title} - ${this.issue.url}` : `${this.issue.title} - `;
  }

  public whereIsItLocatedUrl(): string | null {
    return this.isMobileTypeSignal() ? null : this.issue.url;
  }

  public mainContentBlocks(): IFindingContentBlock[] {
    return this.findingContentBlockService.buildIssueContentBlocks({
      issue: this.issue,
      ruleDetails: this.ruleDetails,
      toolName: this.toolName,
      isMobile: this.isMobileTypeSignal(),
      description: this.displayIssueDescription(),
      selectorId: this.selectorId,
      nodesHTMLId: this.nodesHTMLId,
    });
  }

  public selectorId: string;
  public nodesHTMLId: string;

  public readonly AlertType: typeof AlertType = AlertType;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly IconStyles: typeof IconStyles = IconStyles;

  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private modalService: ModalService,
    private flawService: FlawService,
    private scanReportService: ScanReportService,
    private translateService: TranslateService,
    private userDigitalPropertyService: UserDigitalPropertyService,
    private featureFlagService: FeatureFlagService,
    private findingContentBlockService: FindingContentBlockService,
  ) {
    this.$scanIssues = $scanIssues;
    this.$flaw = $flaw;
    this.url = async (): Promise<string> => {
      return this.scanReportService.getIssueShortLink(
        this.scanId,
        this.toolName,
        this.ruleDetails[$scanIssues.ruleId],
        this.issue.rawFindingId,
      );
    };
    this.AccessibilityAuditToolNames = AccessibilityAuditToolNames;
    this.selectorId = CommonUtility.createUniqueDOMId('selectorId');
    this.nodesHTMLId = CommonUtility.createUniqueDOMId('nodesHTMLId');
  }

  public openColorContrastModal(): void {
    const modalComponentRef: ComponentRef<ColorContrastRuleSuggestionsComponent> = this.modalService.open(
      ColorContrastRuleSuggestionsComponent,
    );

    modalComponentRef.instance.modalInputData = {
      data: this.issue,
    };
  }

  public isColorContrastRule(issue: IAutomatedScanIssue): boolean {
    const data: IScanIssueAuditData = issue[$flaw.data][$auditFinding.auditData];

    const hasColorContrastData = (issueData: IScanIssueAuditData): boolean => {
      return 'contrastBackground' in issueData && 'contrastColor' in issueData;
    };

    return SharedCommonUtility.isNullish(data) === false && hasColorContrastData(data);
  }

  public getFlawPreviousStatus(): string {
    return this.flawService.getPreviousStatus(this.issue);
  }

  public getFlawComponentName(): string {
    if (SharedCommonUtility.notNullish(this.issue?.data?.componentData?.componentName)) {
      return this.issue.data.componentData.componentName;
    }

    const noComponentBody: string = this.translateService.instant('not_associated_with_a_component');
    return `<${noComponentBody}>`;
  }

  public get isElementFromShadowDom(): boolean {
    return ACCESS_ENGINE_SHADOW_DOM_SEPARATOR_MATCHER.test(this.issue.data[$auditFinding.elementReference]);
  }

  public get isElementFromIframe(): boolean {
    return IFRAME_DOM_SEPARATOR_MATCHER.test(this.issue.data[$auditFinding.elementReference]);
  }

  public get isMobileType$(): Observable<boolean> {
    return this.userDigitalPropertyService.isMobileDigitalProperty$.pipe(AngularUtility.shareRef());
  }

  public get locationLabel$(): Observable<string> {
    return this.isMobileType$.pipe(
      map((isMobile: boolean): string => {
        return isMobile ? 'location' : 'label_xpath_or_css';
      }),
    );
  }

  public get elementLabel$(): Observable<string> {
    return this.isMobileType$.pipe(
      map((isMobile: boolean): string => {
        return isMobile ? 'xml_element' : 'audit_rule_property_nodesHTML';
      }),
    );
  }

  protected readonly $auditFinding: typeof $auditFinding = $auditFinding;
}
